/**
 * The UI's half of the tenant lifecycle contract.
 *
 * This mirrors `backend/modules/admin/services/tenant_lifecycle_service.py`.
 * The backend is the authority — it refuses an illegal transition with 409
 * whatever the UI does — but an affordance the server will refuse is a bug in
 * its own right: it invites an operator to attempt something that cannot work
 * and then explains the failure only after the click.
 *
 * Both tenant screens previously decided with a binary
 * `status === 'active' ? Suspend : Reactivate`, so every non-active status —
 * TERMINATED included — rendered a green Reactivate button. Terminated is
 * terminal in ordinary operations, so that button offered an operation the
 * product does not have.
 *
 * Three spellings reach the UI (canonical upper case, legacy lower case, and
 * absent entirely on tenants no operator has ever acted on), so everything
 * here normalises first. Absent means ACTIVE.
 */

export const ACTIVE = 'ACTIVE';
export const SUSPENDED = 'SUSPENDED';
export const READ_ONLY = 'READ_ONLY';
export const TERMINATED = 'TERMINATED';
export const PENDING = 'PENDING';
export const PROVISIONING = 'PROVISIONING';

const LEGACY_ALIASES = {
  ACTIVE, SUSPENDED,
  TRIAL: ACTIVE,
  EXPIRED: READ_ONLY,
  DELETED: TERMINATED,
};

/** Must match ALLOWED_TRANSITIONS in the backend service. */
const ALLOWED = {
  [PENDING]: [PROVISIONING, ACTIVE, TERMINATED],
  [PROVISIONING]: [ACTIVE, PENDING, TERMINATED],
  [ACTIVE]: [SUSPENDED, READ_ONLY, TERMINATED],
  [SUSPENDED]: [ACTIVE, READ_ONLY, TERMINATED],
  [READ_ONLY]: [ACTIVE, SUSPENDED, TERMINATED],
  [TERMINATED]: [],
};

export function normalizeStatus(raw) {
  if (raw === null || raw === undefined) return ACTIVE;
  const text = String(raw).trim().toUpperCase();
  if (!text) return ACTIVE;
  return LEGACY_ALIASES[text] || text;
}

export function canTransition(currentRaw, target) {
  return (ALLOWED[normalizeStatus(currentRaw)] || []).includes(target);
}

/** Terminal in ordinary operations — recovery is a separate privileged op. */
export function isTerminal(currentRaw) {
  return normalizeStatus(currentRaw) === TERMINATED;
}

export function canSuspend(currentRaw) {
  return canTransition(currentRaw, SUSPENDED);
}

export function canReactivate(currentRaw) {
  return canTransition(currentRaw, ACTIVE);
}

export function canSetReadOnly(currentRaw) {
  return canTransition(currentRaw, READ_ONLY);
}

export function canTerminate(currentRaw) {
  return canTransition(currentRaw, TERMINATED);
}

/** Why no lifecycle action is offered, for an honest empty state. */
export function terminalNotice(currentRaw) {
  return isTerminal(currentRaw)
    ? 'This tenant is terminated. Lifecycle actions are unavailable; recovery requires a privileged restore operation.'
    : null;
}
